/**
 * Finance › By property (FINANCE.md §8.2's Properties tab): NOI per property from the statement
 * lines attributed to it, beside its nights, occupancy, ADR and RevPAR — Phase 4's figures next
 * to Phase 5's — and the lines on no property as one more row, never hidden.
 */

import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, type PropertyFinanceResponse } from '../../api.js';
import { EntityPicker, useEntities, useEntityId } from '../../components/EntityPicker.js';
import { formatMoney } from '../../money.js';

function monthsAgo(n: number): string {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() - n);
  return d.toISOString().slice(0, 7);
}

const pct = (value: number | null): string =>
  value === null ? '—' : `${(value * 100).toFixed(1)}%`;
const money = (cents: string | null): string => (cents === null ? '—' : formatMoney(cents));

export function PropertiesFinancePage(): ReactNode {
  const entityId = useEntityId();
  const entities = useEntities();
  const [from, setFrom] = useState(() => monthsAgo(2));
  const [to, setTo] = useState(() => monthsAgo(0));

  const report = useQuery({
    queryKey: ['finance', 'properties', entityId, from, to],
    enabled: entityId !== null && from <= to,
    queryFn: () =>
      api.get<PropertyFinanceResponse>(
        `/api/finance/properties?entity=${entityId}&from=${from}&to=${to}`,
      ),
  });

  const noEntities = entities.data !== undefined && entities.data.entities.length === 0;
  const data = report.data;

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>By property</h1>
          <p className="subtitle">
            What each property earned and spent, its NOI, and the nights behind it. Lines on no
            property are their own row; the entity&apos;s NOI is the properties&apos; plus that.
          </p>
        </div>
      </header>

      <div className="filters">
        <EntityPicker />
        <label>
          From{' '}
          <input type="month" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          To <input type="month" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
        </label>
      </div>

      {(report.isLoading || (entityId === null && entities.isLoading)) && (
        <p className="muted">Loading…</p>
      )}
      {report.isError && <p className="error">Could not load: {report.error.message}</p>}
      {noEntities && (
        <div className="card empty">
          <h2>No entities.</h2>
          <p>
            Run <code>pnpm seed</code> to load the fictional sample portfolio.
          </p>
        </div>
      )}

      {data && (
        <section className="card">
          <h2>
            {data.from} to {data.to}
          </h2>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Market</th>
                  <th className="num">Came in</th>
                  <th className="num">Went out</th>
                  <th className="num">NOI</th>
                  <th className="num">Nights</th>
                  <th className="num">Occupancy</th>
                  <th className="num">ADR</th>
                  <th className="num">RevPAR</th>
                </tr>
              </thead>
              <tbody>
                {data.properties.map((p) => (
                  <tr key={p.property_id ?? 'none'}>
                    <th scope="row">
                      <Link to={`/finance/statements?entity=${data.entity_id}`}>{p.name}</Link>
                      {p.pending_lines > 0 && (
                        <span className="muted small"> · {p.pending_lines} pending</span>
                      )}
                    </th>
                    <td>{p.market_name ?? '—'}</td>
                    <td className="num">{formatMoney(p.revenue_cents)}</td>
                    <td className="num">{formatMoney(p.expenses_cents)}</td>
                    <td className="num">
                      <strong>{formatMoney(p.noi_cents)}</strong>
                    </td>
                    <td
                      className="num"
                      title={`${p.booked_nights ?? 0} booked · ${p.available_nights ?? 0} open · ${p.blocked_nights ?? 0} blocked`}
                    >
                      {p.booked_nights ?? '—'}
                      {p.available_nights !== null && (
                        <span className="muted small">
                          {' '}
                          / {(p.booked_nights ?? 0) + (p.available_nights ?? 0)}
                        </span>
                      )}
                    </td>
                    <td className="num">{pct(p.occupancy)}</td>
                    <td className="num">{money(p.adr_cents)}</td>
                    <td className="num">{money(p.revpar_cents)}</td>
                  </tr>
                ))}
                <tr className="muted">
                  <th scope="row">
                    {data.unattributed.name}
                    {data.unattributed.pending_lines > 0 && (
                      <span className="small"> · {data.unattributed.pending_lines} pending</span>
                    )}
                  </th>
                  <td>—</td>
                  <td className="num">{formatMoney(data.unattributed.revenue_cents)}</td>
                  <td className="num">{formatMoney(data.unattributed.expenses_cents)}</td>
                  <td className="num">{formatMoney(data.unattributed.noi_cents)}</td>
                  <td className="num">—</td>
                  <td className="num">—</td>
                  <td className="num">—</td>
                  <td className="num">—</td>
                </tr>
                <tr className="pnl-figure pnl-net">
                  <th scope="row">Entity</th>
                  <td>—</td>
                  <td className="num">{formatMoney(data.total.revenue_cents)}</td>
                  <td className="num">{formatMoney(data.total.expenses_cents)}</td>
                  <td className="num">{formatMoney(data.total.noi_cents)}</td>
                  <td className="num">
                    {data.total.booked_nights}
                    <span className="muted small">
                      {' '}
                      / {data.total.booked_nights + data.total.available_nights}
                    </span>
                  </td>
                  <td className="num">{pct(data.total.occupancy)}</td>
                  <td className="num">{money(data.total.adr_cents)}</td>
                  <td className="num">{money(data.total.revpar_cents)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="muted small">
            ACTUAL · {data.total.pending_lines} line{data.total.pending_lines === 1 ? '' : 's'} on
            an account not on the chart, in no sum · {data.calc_method}
          </p>
        </section>
      )}
    </div>
  );
}
